import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAIInsightsSidebar } from '~/components/AIInsightsSidebar';
import { conversationsPath, homePath } from '~/constants';

// Module-level flag: true only when the user explicitly clicked the expand button.
// Resets to false on page reload (JS re-executes). Consumed once by ConversationsRoute
// to decide whether to play the expand animation.
let _pendingExpandAnimation = false;

export const markExpandAnimation = () => {
  _pendingExpandAnimation = true;
};

export const consumeExpandAnimation = () => {
  const result = _pendingExpandAnimation;
  _pendingExpandAnimation = false;
  return result;
};

// All navigation decisions for the expanded chat live here.
// Strategy: push only on empty→detail transitions; replace on all other
// in-chat nav. collapseAndReturn pops all chat entries via navigate(-count)
// so pressing Back from the origin page never re-opens the chat.
export const useConversationsNav = () => {
  const navigate = useNavigate();
  const { conversationId, expandedOriginPath, incrementChatEntryCount, decrementChatEntryCount } =
    useAIInsightsSidebar();

  // empty→detail: push; detail→detail: replace
  const navigateToConversation = useCallback(
    (convId: string) => {
      if (conversationId === null) {
        incrementChatEntryCount();
        navigate(`${conversationsPath}/${convId}`, { state: { origin: expandedOriginPath } });
      } else {
        navigate(`${conversationsPath}/${convId}`, {
          replace: true,
          state: { origin: expandedOriginPath },
        });
      }
    },
    [conversationId, expandedOriginPath, incrementChatEntryCount, navigate],
  );

  // detail→empty: replace
  const navigateToNewChat = useCallback(() => {
    navigate(conversationsPath, { replace: true, state: { origin: expandedOriginPath } });
  }, [expandedOriginPath, navigate]);

  // empty→detail triggered by sending first message: push
  const navigateAfterFirstMessage = useCallback(
    (convId: string) => {
      incrementChatEntryCount();
      navigate(`${conversationsPath}/${convId}`, { state: { origin: expandedOriginPath } });
    },
    [expandedOriginPath, incrementChatEntryCount, navigate],
  );

  const collapseAndReturn = useCallback(() => {
    navigate(expandedOriginPath ?? homePath, { replace: true });
  }, [expandedOriginPath, navigate]);

  // call on POP navigation to keep counter in sync
  const syncOnPop = useCallback(() => {
    decrementChatEntryCount();
  }, [decrementChatEntryCount]);

  return { navigateToConversation, navigateToNewChat, navigateAfterFirstMessage, collapseAndReturn, syncOnPop };
};
